if (!customElements.get('header-flyout-menu')) {
  customElements.define(
    'header-flyout-menu',
    class HeaderFlyoutMenu extends HTMLElement {
      constructor() {
        super();
        this.openId = null;
        this.closeTimer = null;
        this.leaveDelay = 140;
        this.header = this.closest('.header-wrapper');
        this.onTriggerEnter = this.onTriggerEnter.bind(this);
        this.onTriggerFocus = this.onTriggerFocus.bind(this);
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onTransitionEnd = this.onTransitionEnd.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
      }

      connectedCallback() {
        this.flyout = this.querySelector('[data-header-flyout]');
        this.viewport = this.querySelector('[data-header-flyout-viewport]');
        this.triggers = Array.from(this.querySelectorAll('[data-flyout-trigger]'));
        this.panels = Array.from(this.querySelectorAll('[data-flyout-panel]'));

        if (!this.flyout || !this.viewport || !this.triggers.length) return;

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('pointerenter', this.onTriggerEnter);
          trigger.addEventListener('focusin', this.onTriggerFocus);
        });

        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('focusout', this.onFocusOut);
        this.viewport.addEventListener('transitionend', this.onTransitionEnd);
        window.addEventListener('resize', this.onWindowResize);
      }

      disconnectedCallback() {
        this.clearCloseTimer();
        this.triggers.forEach((trigger) => {
          trigger.removeEventListener('pointerenter', this.onTriggerEnter);
          trigger.removeEventListener('focusin', this.onTriggerFocus);
        });
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('focusout', this.onFocusOut);
        this.viewport?.removeEventListener('transitionend', this.onTransitionEnd);
        window.removeEventListener('resize', this.onWindowResize);
      }

      get isOpen() {
        return this.openId != null;
      }

      onTriggerEnter(event) {
        if (window.matchMedia('(hover: none)').matches) return;
        this.open(event.currentTarget.dataset.flyoutTrigger);
      }

      onTriggerFocus(event) {
        this.open(event.currentTarget.dataset.flyoutTrigger);
      }

      onPointerEnter() {
        this.clearCloseTimer();
      }

      onPointerLeave() {
        this.scheduleClose();
      }

      onKeyDown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;

        const activeTrigger = this.triggers.find((trigger) => trigger.dataset.flyoutTrigger === this.openId);
        this.close();
        activeTrigger?.focus();
      }

      onFocusOut() {
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      }

      onTransitionEnd(event) {
        if (event.target !== this.viewport || event.propertyName !== 'height') return;

        if (!this.isOpen) {
          this.flyout.hidden = true;
          this.viewport.style.height = '';
        }
      }

      onWindowResize() {
        if (!this.isOpen) return;

        const activePanel = this.panels.find((panel) => panel.dataset.flyoutPanel === this.openId);
        if (!activePanel) return;

        this.viewport.style.height = `${this.measurePanelHeight(activePanel)}px`;
      }

      scheduleClose() {
        this.clearCloseTimer();
        this.closeTimer = setTimeout(() => this.close(), this.leaveDelay);
      }

      clearCloseTimer() {
        if (!this.closeTimer) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      measurePanelHeight(panel) {
        const previousPosition = panel.style.position;
        const previousVisibility = panel.style.visibility;
        const previousDisplay = panel.style.display;
        const wasHidden = panel.hidden;

        panel.hidden = false;
        panel.style.position = 'absolute';
        panel.style.visibility = 'hidden';
        panel.style.display = 'block';

        const height = Math.min(panel.scrollHeight, this.getMaxHeight());

        panel.hidden = wasHidden;
        panel.style.position = previousPosition;
        panel.style.visibility = previousVisibility;
        panel.style.display = previousDisplay;

        return height;
      }

      getMaxHeight() {
        const maxHeight = parseFloat(getComputedStyle(this.viewport).maxHeight);
        return Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : Infinity;
      }

      open(id) {
        if (!id) return;

        this.clearCloseTimer();

        const nextPanel = this.panels.find((panel) => panel.dataset.flyoutPanel === id);
        if (!nextPanel) return;

        const isSwitch = this.isOpen && this.openId !== id;
        const isFirstOpen = !this.isOpen;
        const currentHeight = isFirstOpen ? 0 : this.viewport.getBoundingClientRect().height;

        this.openId = id;
        this.classList.toggle('is-switching', isSwitch);
        this.classList.add('is-open');
        this.flyout.hidden = false;

        if (this.header) this.header.preventHide = true;

        this.triggers.forEach((trigger) => {
          const active = trigger.dataset.flyoutTrigger === id;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
        });

        this.panels.forEach((panel) => {
          const active = panel === nextPanel;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });

        const nextHeight = this.measurePanelHeight(nextPanel);

        this.viewport.style.height = `${currentHeight}px`;
        this.viewport.offsetHeight;
        this.viewport.style.height = `${nextHeight}px`;
      }

      close() {
        this.clearCloseTimer();
        if (!this.isOpen) return;

        const currentHeight = this.viewport.getBoundingClientRect().height;

        this.openId = null;
        this.classList.remove('is-open', 'is-switching');

        if (this.header) this.header.preventHide = false;

        this.triggers.forEach((trigger) => {
          trigger.classList.remove('is-active');
          trigger.setAttribute('aria-expanded', 'false');
        });

        this.viewport.style.height = `${currentHeight}px`;
        this.viewport.offsetHeight;
        this.viewport.style.height = '0px';
      }
    }
  );
}
